import React, { useMemo, useState, useCallback } from 'react';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { WarehouseLayout } from '@components/layout/WarehouseLayout';
import { useWarehouseContext } from '@components/layout/WarehouseContext';
import {
  useMaterialCatalog,
  useStockLevels,
  useMaterialCategories,
  useEquipmentAssets,
  useStockTransactions,
} from '@hooks/api/warehouse';
import {
  MaterialCatalog,
  StockTransaction,
  TransactionType,
} from '@models/warehouse.model';
import AssessmentIcon from '@mui/icons-material/Assessment';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ConstructionIcon from '@mui/icons-material/Construction';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatCompactCurrency, formatFullCurrency } from '@utils/formatCurrency';
import { formatDateOnly } from '@utils/formatDate';
import { generateInventoryValuationPdf } from '@utils/inventoryValuationPdf';
import { useTranslation } from '@hooks/useTranslation';

// ==================== TYPES ====================

interface MaterialValuation extends MaterialCatalog {
  totalOnHand: number;
  totalReserved: number;
  totalValue: number;
  categoryName?: string;
  categoryColor?: string;
}

interface CategoryValuation {
  categoryId: string;
  name: string;
  color: string;
  itemCount: number;
  totalValue: number;
  percentage: number;
}

interface ProjectCostSummary {
  projectId: string;
  projectName: string;
  totalCost: number;
  totalQuantity: number;
  transactionCount: number;
}

// ==================== CONSTANTS ====================

const ITEMS_PER_PAGE = 10;

const COLOR_MAP: Record<string, string> = {
  blue: 'bg-blue-500',
  purple: 'bg-purple-500',
  orange: 'bg-orange-500',
  cyan: 'bg-cyan-500',
  yellow: 'bg-yellow-500',
  green: 'bg-green-500',
  indigo: 'bg-indigo-500',
  gray: 'bg-gray-400',
};

const BAR_COLOR_MAP: Record<string, string> = {
  blue: 'bg-blue-400',
  purple: 'bg-purple-400',
  orange: 'bg-orange-400',
  cyan: 'bg-cyan-400',
  yellow: 'bg-yellow-400',
  green: 'bg-green-400',
  indigo: 'bg-indigo-400',
  gray: 'bg-gray-300',
};

// Sprint 30, story-152. Reads canonical Spanish from the shared `enumLabels` section
// (docs/architecture.md Sprint 30) instead of a hardcoded English label table — the exhaustive/
// catalog-fallback house pattern story-148 established, applied here for the first time to this
// report's own transaction-kind vocabulary (distinct from the `TransactionType` badge family's
// past-participle labels in §5a — this report needs noun forms for a "kind of row" column).
const getTransactionTypeConfig = (
  tEnumLabels: ReturnType<typeof useTranslation<'enumLabels'>>,
): Record<string, { label: string; color: string; icon: React.ReactNode }> => ({
  RECEIVE: { label: tEnumLabels.transactionKindPurchase, color: 'text-green-700 bg-green-50', icon: <CallReceivedIcon sx={{ fontSize: 14 }} /> },
  CONSUME: { label: tEnumLabels.transactionKindDraw, color: 'text-red-700 bg-red-50', icon: <CallMadeIcon sx={{ fontSize: 14 }} /> },
  ADJUST: { label: tEnumLabels.transactionKindAdjustment, color: 'text-amber-700 bg-amber-50', icon: <WarningAmberIcon sx={{ fontSize: 14 }} /> },
  TRANSFER: { label: tEnumLabels.transactionKindTransfer, color: 'text-blue-700 bg-blue-50', icon: <SwapHorizIcon sx={{ fontSize: 14 }} /> },
  RETURN: { label: tEnumLabels.transactionKindReturn, color: 'text-indigo-700 bg-indigo-50', icon: <CallReceivedIcon sx={{ fontSize: 14 }} /> },
  RESERVE: { label: tEnumLabels.transactionKindReserve, color: 'text-purple-700 bg-purple-50', icon: <Inventory2Icon sx={{ fontSize: 14 }} /> },
  RELEASE_RESERVE: { label: tEnumLabels.transactionKindRelease, color: 'text-gray-700 bg-gray-50', icon: <Inventory2Icon sx={{ fontSize: 14 }} /> },
  RECOVERY: { label: tEnumLabels.transactionKindRecovery, color: 'text-teal-700 bg-teal-50', icon: <CallReceivedIcon sx={{ fontSize: 14 }} /> },
});

// ==================== UTILITY ====================

const formatCurrencyValue = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US').format(value);

/**
 * Calculates the Weighted Average Cost from a list of RECEIVE transactions.
 * Uses raw floats for precision, display with toFixed(2).
 */
const calculateWAC = (receiveTransactions: StockTransaction[]): number => {
  if (!receiveTransactions || receiveTransactions.length === 0) return 0;
  const totals = receiveTransactions.reduce(
    (acc, tx) => {
      const qty = Number(tx.quantity) || 0;
      const cost = Number(tx.unitCost) || 0;
      acc.totalValue += qty * cost;
      acc.totalQty += qty;
      return acc;
    },
    { totalValue: 0, totalQty: 0 },
  );
  return totals.totalQty === 0 ? 0 : totals.totalValue / totals.totalQty;
};

// ==================== MATERIAL DETAIL VIEW ====================

interface MaterialDetailViewProps {
  material: MaterialValuation;
  transactions: StockTransaction[];
  onBack: () => void;
}

const MaterialDetailView: React.FC<MaterialDetailViewProps> = ({ material, transactions, onBack }) => {
  const tWarehouse = useTranslation('warehouse');
  const tCommon = useTranslation('common');
  const tEnumLabels = useTranslation('enumLabels');
  const tProjects = useTranslation('projects');
  const tTaskDetail = useTranslation('taskDetail');
  const transactionTypeConfig = useMemo(() => getTransactionTypeConfig(tEnumLabels), [tEnumLabels]);
  const materialTransactions = useMemo(
    () => transactions.filter((tx) => tx.catalogId === material.catalogId),
    [transactions, material.catalogId],
  );

  const receiveTransactions = useMemo(
    () => materialTransactions
      .filter((tx) => tx.type === TransactionType.RECEIVE)
      .sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()),
    [materialTransactions],
  );

  const outTransactions = useMemo(
    () => materialTransactions
      .filter((tx) => [TransactionType.CONSUME, TransactionType.ADJUST, TransactionType.TRANSFER].includes(tx.type))
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()),
    [materialTransactions],
  );

  const wac = useMemo(() => calculateWAC(receiveTransactions), [receiveTransactions]);

  const totalPurchased = useMemo(
    () => receiveTransactions.reduce((sum, tx) => sum + (Number(tx.quantity) || 0), 0),
    [receiveTransactions],
  );

  const totalConsumed = useMemo(
    () => materialTransactions
      .filter((tx) => tx.type === TransactionType.CONSUME)
      .reduce((sum, tx) => sum + (Number(tx.quantity) || 0), 0),
    [materialTransactions],
  );

  const totalConsumedValue = useMemo(
    () => materialTransactions
      .filter((tx) => tx.type === TransactionType.CONSUME)
      .reduce((sum, tx) => sum + (Number(tx.totalCost) || 0), 0),
    [materialTransactions],
  );

  // Job costing: group CONSUME transactions by project
  const projectCosts = useMemo<ProjectCostSummary[]>(() => {
    const map = new Map<string, ProjectCostSummary>();
    materialTransactions
      .filter((tx) => tx.type === TransactionType.CONSUME && tx.projectId)
      .forEach((tx) => {
        const key = tx.projectId!;
        const existing = map.get(key);
        if (existing) {
          existing.totalCost += Number(tx.totalCost) || 0;
          existing.totalQuantity += Number(tx.quantity) || 0;
          existing.transactionCount += 1;
        } else {
          map.set(key, {
            projectId: key,
            projectName: tx.project?.name || 'Unknown Project',
            totalCost: Number(tx.totalCost) || 0,
            totalQuantity: Number(tx.quantity) || 0,
            transactionCount: 1,
          });
        }
      });
    return Array.from(map.values()).sort((a, b) => b.totalCost - a.totalCost);
  }, [materialTransactions]);

  return (
    <div className="space-y-6">
      {/* Back button + Header */}
      <div className="flex items-center gap-3">
        <button type="button"
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ArrowBackIcon sx={{ fontSize: 20 }} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{material.name}</h1>
          <p className="text-sm text-gray-500">
            {material.sku ? `SKU: ${material.sku}` : ''}{' '}
            {material.categoryName && (
              <span className="inline-flex items-center gap-1 ml-2">
                <span className={`w-2 h-2 rounded-full ${COLOR_MAP[material.categoryColor || 'gray'] || COLOR_MAP.gray}`} />
                {material.categoryName}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* WAC Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <p className="text-xs text-blue-600 uppercase font-semibold">{tWarehouse.valuation.totalStock}</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatNumber(material.totalOnHand)}{' '}
            <span className="text-sm font-normal text-gray-500">{material.unit || tWarehouse.genericUnitPlural}</span>
          </p>
          {material.totalReserved > 0 && (
            <p className="text-xs text-amber-600 mt-1">
              {/* KNOWN GAP (sprint 30, story-152 change-request review, Warning #1): bare
                  English JSX-text word next to a computed value — same blind-spot shape the
                  guard's bare-single-word carve-out can't see (docs/architecture.md's i18n
                  guard design, Addendum 7's sibling blind spots). Deliberately deferred, out of
                  this change's named scope; needs a platform proposal to extend the detector
                  (or a future i18n pass) before it's fixed, not a silent leftover. */}
              {formatNumber(material.totalReserved)} reserved
            </p>
          )}
        </div>
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <p className="text-xs text-green-600 uppercase font-semibold">{tWarehouse.valuation.weightedAvgCost}</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrencyValue(wac)}{' '}
            <span className="text-sm font-normal text-gray-500">/{material.unit || tWarehouse.genericUnitSingular}</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {tWarehouse.valuation.current} {formatCurrencyValue(material.averageCost || 0)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <p className="text-xs text-indigo-600 uppercase font-semibold">{tWarehouse.valuation.inventoryValue}</p>
          <p className="text-2xl font-bold text-gray-900" title={formatFullCurrency(material.totalValue)}>
            {formatCompactCurrency(material.totalValue)}
          </p>
          <p className="text-xs text-gray-400 mt-1">{tWarehouse.valuation.qtyXWac}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <p className="text-xs text-red-600 uppercase font-semibold">{tWarehouse.valuation.totalConsumed}</p>
          <p className="text-2xl font-bold text-gray-900" title={formatFullCurrency(totalConsumedValue)}>
            {formatCompactCurrency(totalConsumedValue)}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {/* KNOWN GAP (sprint 30, story-152 change-request review, Warning #1): same bare
                English JSX-text-sibling shape as "reserved" above — see that comment. */}
            {formatNumber(totalConsumed)} {material.unit || tWarehouse.genericUnitPlural} drawn
          </p>
        </div>
      </div>

      {/* WAC Calculation Breakdown */}
      {receiveTransactions.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{tWarehouse.valuation.wacCalculation}</h2>
            <p className="text-xs text-gray-500 mt-1">
              {tWarehouse.valuation.wacFormula}
            </p>
          </div>
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {receiveTransactions.map((tx, i) => (
                <React.Fragment key={tx.transactionId}>
                  {i > 0 && <span className="text-gray-400">+</span>}
                  <span className="bg-white px-2 py-1 rounded border border-gray-200 text-gray-700">
                    {formatNumber(Number(tx.quantity))} x {formatCurrencyValue(Number(tx.unitCost))}
                  </span>
                </React.Fragment>
              ))}
              <span className="text-gray-400">=</span>
              <span className="font-semibold text-gray-900">
                {formatCurrencyValue(receiveTransactions.reduce((s, tx) => s + (Number(tx.quantity) * Number(tx.unitCost)), 0))}
              </span>
              <span className="text-gray-400">/</span>
              <span className="font-semibold text-gray-900">
                {formatNumber(totalPurchased)} {material.unit || tWarehouse.genericUnitPlural}
              </span>
              <span className="text-gray-400">=</span>
              <span className="font-bold text-green-700 bg-green-50 px-2 py-1 rounded">
                {formatCurrencyValue(wac)} / {material.unit || tWarehouse.genericUnitSingular}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Purchase History (IN) */}
      <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
          <CallReceivedIcon className="text-green-600" sx={{ fontSize: 20 }} />
          <h2 className="text-lg font-semibold text-gray-900">{tWarehouse.valuation.purchaseHistory}</h2>
          <span className="text-sm text-gray-400 ml-auto">{receiveTransactions.length} purchases</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tWarehouse.dashboard.date}</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tCommon.quantity}</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tWarehouse.unitCost}</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tTaskDetail.inventory.total}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tWarehouse.alerts.supplier}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tWarehouse.reference}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {receiveTransactions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500">
                    {tWarehouse.valuation.noPurchaseTransactions}
                  </td>
                </tr>
              ) : (
                receiveTransactions.map((tx) => (
                  <tr key={tx.transactionId} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-900">{formatDateOnly(tx.createdAt)}</td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right">
                      {formatNumber(Number(tx.quantity))} {material.unit || tWarehouse.genericUnitPlural}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right">{formatCurrencyValue(Number(tx.unitCost))}</td>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right">
                      {formatCurrencyValue(Number(tx.totalCost))}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600">{tx.supplier || '-'}</td>
                    <td className="px-6 py-3 text-sm text-gray-600">{tx.referenceNumber || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
            {receiveTransactions.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 border-t border-gray-200">
                  <td className="px-6 py-3 text-sm font-semibold text-gray-900">{tWarehouse.valuation.totalPurchased}</td>
                  <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                    {formatNumber(totalPurchased)} {material.unit || tWarehouse.genericUnitPlural}
                  </td>
                  <td className="px-6 py-3 text-sm font-semibold text-green-700 text-right">
                    {tWarehouse.valuation.wacColon} {formatCurrencyValue(wac)}
                  </td>
                  <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                    {formatCurrencyValue(receiveTransactions.reduce((s, tx) => s + (Number(tx.totalCost) || 0), 0))}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Usage / Draw History (OUT) */}
      <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
          <CallMadeIcon className="text-red-600" sx={{ fontSize: 20 }} />
          <h2 className="text-lg font-semibold text-gray-900">{tWarehouse.valuation.usageHistory}</h2>
          <span className="text-sm text-gray-400 ml-auto">{outTransactions.length} transactions</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tWarehouse.dashboard.date}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tWarehouse.dashboard.typeColumn}</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tCommon.quantity}</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tWarehouse.unitCost}</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tTaskDetail.inventory.total}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tWarehouse.valuation.projectTask}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tWarehouse.equipmentDetail.notes}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {outTransactions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">
                    {tWarehouse.valuation.noUsageTransactions}
                  </td>
                </tr>
              ) : (
                outTransactions.map((tx) => {
                  const config = transactionTypeConfig[tx.type] || transactionTypeConfig.ADJUST;
                  return (
                    <tr key={tx.transactionId} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm text-gray-900">{formatDateOnly(tx.createdAt)}</td>
                      <td className="px-6 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
                          {config.icon}
                          {config.label}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-900 text-right">
                        {formatNumber(Math.abs(Number(tx.quantity)))} {material.unit || tWarehouse.genericUnitPlural}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-900 text-right">
                        {tx.unitCost ? formatCurrencyValue(Number(tx.unitCost)) : '-'}
                      </td>
                      <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right">
                        {tx.totalCost ? formatCurrencyValue(Number(tx.totalCost)) : '-'}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-600">
                        {tx.project?.name || tx.task?.name || '-'}
                        {tx.project?.name && tx.task?.name && (
                          <span className="text-xs text-gray-400 block">{tx.task.name}</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-500 max-w-[200px] truncate" title={tx.notes || ''}>
                        {tx.reason || tx.notes || '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Job Costing by Project */}
      {projectCosts.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
            <AccountTreeIcon className="text-indigo-600" sx={{ fontSize: 20 }} />
            <h2 className="text-lg font-semibold text-gray-900">{tWarehouse.valuation.costByProject}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tProjects.project}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tWarehouse.valuation.quantityUsed}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tWarehouse.valuation.totalCost}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tWarehouse.valuation.draws}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {projectCosts.map((pc) => (
                  <tr key={pc.projectId} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">{pc.projectName}</td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right">
                      {formatNumber(pc.totalQuantity)} {material.unit || tWarehouse.genericUnitPlural}
                    </td>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right">
                      {formatCurrencyValue(pc.totalCost)}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600 text-right">{pc.transactionCount}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 border-t border-gray-200">
                  <td className="px-6 py-3 text-sm font-semibold text-gray-900">{tTaskDetail.inventory.total}</td>
                  <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                    {formatNumber(projectCosts.reduce((s, pc) => s + pc.totalQuantity, 0))} {material.unit || tWarehouse.genericUnitPlural}
                  </td>
                  <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                    {formatCurrencyValue(projectCosts.reduce((s, pc) => s + pc.totalCost, 0))}
                  </td>
                  <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                    {projectCosts.reduce((s, pc) => s + pc.transactionCount, 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN REPORT CONTENT ====================

const InventoryValuationReportContent: React.FC = () => {
  const tWarehouse = useTranslation('warehouse');
  const tCommon = useTranslation('common');
  const tProjects = useTranslation('projects');
  const tTaskDetail = useTranslation('taskDetail');
  const tEnumLabels = useTranslation('enumLabels');
  const { selectedWarehouse } = useWarehouseContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialValuation | null>(null);

  // API Hooks
  const {
    data: materials = [],
    isLoading: materialsLoading,
    isError: materialsError,
  } = useMaterialCatalog();
  const {
    data: stockLevels = [],
    isLoading: stockLoading,
    isError: stockError,
  } = useStockLevels(selectedWarehouse?.warehouseId);
  const {
    data: categories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = useMaterialCategories();
  const {
    data: equipmentAssets = [],
    isLoading: equipmentLoading,
    isError: equipmentError,
  } = useEquipmentAssets();

  // Fetch all transactions for the selected warehouse (higher limit for report)
  const {
    data: allTransactions = [],
    isLoading: transactionsLoading,
  } = useStockTransactions({
    warehouseId: selectedWarehouse?.warehouseId,
    limit: 500,
  });

  const isLoading =
    (materialsLoading && !materialsError) ||
    (stockLoading && !stockError) ||
    (categoriesLoading && !categoriesError) ||
    (equipmentLoading && !equipmentError) ||
    transactionsLoading;

  // Calculate material valuations
  const materialValuations = useMemo<MaterialValuation[]>(() => {
    return materials
      .filter((m) => m.isActive)
      .map((material) => {
        const materialStockLevels = stockLevels.filter(
          (sl) => sl.catalogId === material.catalogId,
        );
        const totalOnHand = materialStockLevels.reduce(
          (sum, sl) => sum + (Number(sl.quantityOnHand) || 0),
          0,
        );
        const totalReserved = materialStockLevels.reduce(
          (sum, sl) => sum + (Number(sl.quantityReserved) || 0),
          0,
        );
        const avgCost =
          materialStockLevels.length > 0
            ? materialStockLevels.reduce((sum, sl) => sum + (Number(sl.averageCost) || 0), 0) /
              materialStockLevels.length
            : Number(material.averageCost) || 0;
        const totalValue = totalOnHand * avgCost;
        const category = categories.find((c) => c.categoryId === material.categoryId);

        return {
          ...material,
          totalOnHand,
          totalReserved,
          totalValue,
          averageCost: avgCost,
          categoryName: category?.name,
          categoryColor: category?.color,
        };
      })
      .sort((a, b) => b.totalValue - a.totalValue);
  }, [materials, stockLevels, categories]);

  // Category valuations
  const categoryValuations = useMemo<CategoryValuation[]>(() => {
    const totalInventoryValue = materialValuations.reduce(
      (sum, m) => sum + m.totalValue,
      0,
    );

    const categoryMap = new Map<string, CategoryValuation>();

    materialValuations.forEach((material) => {
      const catId = material.categoryId || 'uncategorized';
      const existing = categoryMap.get(catId);

      if (existing) {
        existing.itemCount += 1;
        existing.totalValue += material.totalValue;
      } else {
        const category = categories.find((c) => c.categoryId === catId);
        categoryMap.set(catId, {
          categoryId: catId,
          name: category?.name || 'Uncategorized',
          color: category?.color || 'gray',
          itemCount: 1,
          totalValue: material.totalValue,
          percentage: 0,
        });
      }
    });

    return Array.from(categoryMap.values())
      .map((cat) => ({
        ...cat,
        percentage: totalInventoryValue > 0 ? (cat.totalValue / totalInventoryValue) * 100 : 0,
      }))
      .sort((a, b) => b.totalValue - a.totalValue);
  }, [materialValuations, categories]);

  // Summary stats
  const summary = useMemo(() => {
    const totalMaterialValue = materialValuations.reduce(
      (sum, m) => sum + m.totalValue,
      0,
    );
    const activeItems = materialValuations.length;
    const avgItemValue = activeItems > 0 ? totalMaterialValue / activeItems : 0;
    const totalEquipmentValue = equipmentAssets.reduce(
      (sum, eq) => sum + (Number(eq.purchaseCost) || 0),
      0,
    );
    const totalConsumedValue = allTransactions
      .filter((tx) => tx.type === TransactionType.CONSUME)
      .reduce((sum, tx) => sum + (Number(tx.totalCost) || 0), 0);

    return {
      totalMaterialValue,
      activeItems,
      avgItemValue,
      totalEquipmentValue,
      equipmentCount: equipmentAssets.length,
      totalConsumedValue,
    };
  }, [materialValuations, equipmentAssets, allTransactions]);

  // Job costing: group ALL consume transactions by project
  const globalProjectCosts = useMemo<ProjectCostSummary[]>(() => {
    const map = new Map<string, ProjectCostSummary>();
    allTransactions
      .filter((tx) => tx.type === TransactionType.CONSUME && tx.projectId)
      .forEach((tx) => {
        const key = tx.projectId!;
        const existing = map.get(key);
        if (existing) {
          existing.totalCost += Number(tx.totalCost) || 0;
          existing.totalQuantity += Number(tx.quantity) || 0;
          existing.transactionCount += 1;
        } else {
          map.set(key, {
            projectId: key,
            projectName: tx.project?.name || 'Unknown Project',
            totalCost: Number(tx.totalCost) || 0,
            totalQuantity: Number(tx.quantity) || 0,
            transactionCount: 1,
          });
        }
      });
    return Array.from(map.values()).sort((a, b) => b.totalCost - a.totalCost);
  }, [allTransactions]);

  // Pagination
  const totalPages = Math.ceil(materialValuations.length / ITEMS_PER_PAGE);
  const paginatedMaterials = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return materialValuations.slice(start, start + ITEMS_PER_PAGE);
  }, [materialValuations, currentPage]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleExportPdf = useCallback(() => {
    generateInventoryValuationPdf({
      summary,
      materialValuations: materialValuations.map((m) => ({
        name: m.name,
        sku: m.sku || '',
        categoryName: m.categoryName || '',
        totalOnHand: m.totalOnHand,
        totalReserved: m.totalReserved,
        averageCost: m.averageCost || 0,
        totalValue: m.totalValue,
        unit: m.unit || tWarehouse.genericUnitPlural,
      })),
      categoryValuations: categoryValuations.map((c) => ({
        name: c.name,
        itemCount: c.itemCount,
        totalValue: c.totalValue,
        percentage: c.percentage,
      })),
      projectCosts: globalProjectCosts.map((pc) => ({
        projectName: pc.projectName,
        transactionCount: pc.transactionCount,
        totalCost: pc.totalCost,
      })),
    });
  }, [summary, materialValuations, categoryValuations, globalProjectCosts, tWarehouse.genericUnitPlural]);

  const handleSelectMaterial = useCallback((material: MaterialValuation) => {
    setSelectedMaterial(material);
    const scrollableParent = document.querySelector('main.overflow-y-auto');
    if (scrollableParent) {
      scrollableParent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const handleBackToOverview = useCallback(() => {
    setSelectedMaterial(null);
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loading size="3xl" />
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // ==================== MATERIAL DETAIL VIEW ====================
  if (selectedMaterial) {
    return (
      <MaterialDetailView
        material={selectedMaterial}
        transactions={allTransactions}
        onBack={handleBackToOverview}
      />
    );
  }

  // ==================== OVERVIEW ====================
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <AssessmentIcon className="text-indigo-600" sx={{ fontSize: 28 }} />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {tWarehouse.valuation.inventoryValuationReport}
            </h1>
            <p className="text-sm text-gray-500">{tWarehouse.valuation.asOf} {today}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="primary"
            leftIcon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />}
            onClick={handleExportPdf}
          >
            {tWarehouse.valuation.exportPdf}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{tWarehouse.valuation.totalInventoryValue}</p>
              <p className="text-2xl font-bold text-gray-900 truncate" title={formatFullCurrency(summary.totalMaterialValue)}>
                {formatCompactCurrency(summary.totalMaterialValue)}
              </p>
            </div>
            <div className="w-10 h-10 flex-shrink-0 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUpIcon className="text-green-600" sx={{ fontSize: 20 }} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{tWarehouse.alerts.totalItems}</p>
              <p className="text-2xl font-bold text-gray-900">{summary.activeItems}</p>
              <p className="text-xs text-gray-400">{tWarehouse.valuation.activeCatalogItems}</p>
            </div>
            <div className="w-10 h-10 flex-shrink-0 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Inventory2Icon className="text-indigo-600" sx={{ fontSize: 20 }} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{tWarehouse.valuation.totalConsumedValue}</p>
              <p className="text-2xl font-bold text-gray-900 truncate" title={formatFullCurrency(summary.totalConsumedValue)}>
                {formatCompactCurrency(summary.totalConsumedValue)}
              </p>
              <p className="text-xs text-gray-400">{tWarehouse.valuation.materialsDrawnToProjects}</p>
            </div>
            <div className="w-10 h-10 flex-shrink-0 bg-red-100 rounded-lg flex items-center justify-center">
              <CallMadeIcon className="text-red-600" sx={{ fontSize: 20 }} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{tWarehouse.valuation.equipmentAssets}</p>
              <p className="text-2xl font-bold text-gray-900 truncate" title={formatFullCurrency(summary.totalEquipmentValue)}>
                {formatCompactCurrency(summary.totalEquipmentValue)}
              </p>
              <p className="text-xs text-gray-400">{summary.equipmentCount} {tWarehouse.valuation.assetsTotal}</p>
            </div>
            <div className="w-10 h-10 flex-shrink-0 bg-amber-100 rounded-lg flex items-center justify-center">
              <ConstructionIcon className="text-amber-600" sx={{ fontSize: 20 }} />
            </div>
          </div>
        </div>
      </div>

      {/* Valuation by Category */}
      <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{tWarehouse.valuation.valuationByCategory}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tCommon.category}</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tWarehouse.valuation.items}</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tWarehouse.alerts.totalValue}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-64">{tWarehouse.valuation.pctOfTotal}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {categoryValuations.map((cat) => (
                <tr key={cat.categoryId} className="hover:bg-gray-50">
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded ${COLOR_MAP[cat.color] || COLOR_MAP.gray}`} />
                      <span className="text-sm font-medium text-gray-900">{cat.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-600 text-right">{cat.itemCount}</td>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right">{formatCurrencyValue(cat.totalValue)}</td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-gray-100 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${BAR_COLOR_MAP[cat.color] || BAR_COLOR_MAP.gray}`}
                          style={{ width: `${Math.min(cat.percentage, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 w-12 text-right">{cat.percentage.toFixed(1)}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 border-t border-gray-200">
                <td className="px-6 py-3 text-sm font-semibold text-gray-900">{tWarehouse.valuation.totalMaterials}</td>
                <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">{summary.activeItems}</td>
                <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">{formatCurrencyValue(summary.totalMaterialValue)}</td>
                <td className="px-6 py-3 text-sm font-semibold text-gray-500 text-right">100%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Detailed Inventory Breakdown */}
      <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{tWarehouse.valuation.detailedInventoryBreakdown}</h2>
          <p className="text-xs text-gray-500 mt-1">{tWarehouse.valuation.clickMaterialHelp}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tTaskDetail.materials.colMaterial}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tCommon.category}</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tWarehouse.valuation.qtyOnHand}</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tEnumLabels.equipmentStatusReserved}</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tWarehouse.valuation.avgUnitCost}</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{tWarehouse.alerts.totalValue}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedMaterials.map((material) => (
                <tr
                  key={material.catalogId}
                  className="hover:bg-indigo-50 cursor-pointer transition-colors"
                  onClick={() => handleSelectMaterial(material)}
                >
                  <td className="px-6 py-3">
                    <div>
                      <p className="text-sm font-medium text-indigo-600 hover:text-indigo-800">{material.name}</p>
                      <p className="text-xs text-gray-500">{material.sku || '-'}</p>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    {material.categoryName ? (
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${COLOR_MAP[material.categoryColor || 'gray'] || COLOR_MAP.gray}`} />
                        <span className="text-xs text-gray-600">{material.categoryName}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-900 text-right">
                    {formatNumber(material.totalOnHand)}{' '}
                    <span className="text-xs text-gray-500">{material.unit || tWarehouse.genericUnitPlural}</span>
                  </td>
                  <td className="px-6 py-3 text-sm text-right">
                    {material.totalReserved > 0 ? (
                      <span className="text-amber-600">{formatNumber(material.totalReserved)}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-900 text-right">{formatCurrencyValue(material.averageCost || 0)}</td>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right">{formatCurrencyValue(material.totalValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {tWarehouse.equipmentDetail.showing}{' '}
              <span className="font-medium text-gray-900">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span>{' '}
              {tWarehouse.equipmentDetail.to}{' '}
              <span className="font-medium text-gray-900">{Math.min(currentPage * ITEMS_PER_PAGE, materialValuations.length)}</span>{' '}
              {tWarehouse.equipmentDetail.of}{' '}
              <span className="font-medium text-gray-900">{materialValuations.length}</span>{' '}
              {tWarehouse.valuation.itemsSuffix}
            </div>
            <div className="flex items-center gap-2">
              <button type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                {tCommon.previous}
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button type="button"
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                      currentPage === pageNum
                        ? 'bg-indigo-600 text-white'
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {tCommon.next}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {materialValuations.length === 0 && (
          <div className="py-12 text-center text-gray-500">
            <Inventory2Icon sx={{ fontSize: 48 }} className="text-gray-300 mb-2" />
            <p className="text-sm">{tWarehouse.valuation.noInventoryData}</p>
          </div>
        )}
      </div>

      {/* Job Costing Summary by Project */}
      {globalProjectCosts.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
            <AccountTreeIcon className="text-indigo-600" sx={{ fontSize: 20 }} />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{tWarehouse.valuation.materialCostByProject}</h2>
              <p className="text-xs text-gray-500 mt-0.5">{tWarehouse.valuation.totalMaterialConsumedPerProject}</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tProjects.project}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tWarehouse.valuation.draws}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">{tWarehouse.valuation.totalMaterialCost}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {globalProjectCosts.map((pc) => (
                  <tr key={pc.projectId} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">{pc.projectName}</td>
                    <td className="px-6 py-3 text-sm text-gray-600 text-right">{pc.transactionCount}</td>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right">{formatCurrencyValue(pc.totalCost)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 border-t border-gray-200">
                  <td className="px-6 py-3 text-sm font-semibold text-gray-900">{tTaskDetail.inventory.total}</td>
                  <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                    {globalProjectCosts.reduce((s, pc) => s + pc.transactionCount, 0)}
                  </td>
                  <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                    {formatCurrencyValue(globalProjectCosts.reduce((s, pc) => s + pc.totalCost, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== PAGE ====================

const InventoryValuationReportPage: React.FC = () => {
  return (
    <WarehouseLayout>
      <InventoryValuationReportContent />
    </WarehouseLayout>
  );
};

export default InventoryValuationReportPage;
